/**
 * NovelInfoCard - 小說資訊卡片
 * 包含封面、標題、作者、統計、操作按鈕
 */

import { Link } from 'react-router-dom';
import type { Novel, Chapter, UserReadingProgress } from '../../services/types';
import { statusConfig } from './types';
import { CoverImage } from '../CoverImage';

export interface NovelInfoCardProps {
  novel: Novel;
  completedChapters: Chapter[];
  lastReadChapter: Chapter | null;
  userProgress: UserReadingProgress | null;
  totalWords: number;
  isFavorited: boolean;
  favoriteLoading: boolean;
  downloading: boolean;
  recrawling: boolean;
  isAdmin: boolean;
  onToggleFavorite: () => void;
  onDownload: (format: 'zip' | 'txt') => void;
  onPause: () => void;
  onResume: () => void;
  onDelete: () => void;
  onRecrawlEmpty: () => void;
  onShowNovelEditor: () => void;
  onShowSeriesManager: () => void;
  onShowSyncChapters: () => void;
  hasEmptyChapters: boolean;
}

export function NovelInfoCard({
  novel,
  completedChapters,
  lastReadChapter,
  userProgress,
  totalWords,
  isFavorited,
  favoriteLoading,
  downloading,
  recrawling,
  isAdmin,
  onToggleFavorite,
  onDownload,
  onPause,
  onResume,
  onDelete,
  onRecrawlEmpty,
  onShowNovelEditor,
  onShowSeriesManager,
  onShowSyncChapters,
  hasEmptyChapters,
}: NovelInfoCardProps) {
  const config = statusConfig[novel.status];

  return (
    <div className="card overflow-hidden">
      <div className="md:flex">
        {/* 封面區域 */}
        <div className="w-full md:w-56 md:flex-shrink-0">
          <div className="relative aspect-[3/4] bg-neutral-100 dark:bg-neutral-800">
            <CoverImage
              src={novel.cover_url}
              alt={novel.title}
              className="w-full h-full object-cover"
              fallback={
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <span className="text-3xl text-primary-600 dark:text-primary-400 font-bold">
                      {novel.title.charAt(0)}
                    </span>
                  </div>
                </div>
              }
            />
            {/* 狀態標籤 */}
            <div className="absolute top-3 left-3">
              <span className={`badge ${config.badge}`}>{config.label}</span>
            </div>
          </div>
        </div>

        {/* 資訊區域 */}
        <div className="flex-1 p-6">
          <div className="flex flex-col h-full">
            {/* 標題和作者 */}
            <div className="mb-4">
              <h1 className="heading-page mb-2">{novel.title}</h1>
              {novel.author && (
                <p className="body-base">
                  作者:{' '}
                  <Link
                    to={`/author/${encodeURIComponent(novel.author)}`}
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {novel.author}
                  </Link>
                </p>
              )}
              {/* 所屬系列 */}
              {novel.series && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="body-small">系列:</span>
                  <Link to={`/series/${novel.series.id}`} className="badge badge-primary">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    {novel.series.title}
                    {novel.series.novel_count > 1 && (
                      <span className="text-primary-500 dark:text-primary-300">
                        (第 {novel.series_order} / {novel.series.novel_count} 本)
                      </span>
                    )}
                  </Link>
                </div>
              )}
            </div>

            {/* 簡介 */}
            {novel.description && (
              <p className="body-small mb-4 line-clamp-3">{novel.description}</p>
            )}

            {/* 統計資訊 */}
            <div className="flex flex-wrap gap-4 body-small mb-4">
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span>
                  {completedChapters.length} / {novel.total_chapters} 章
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                  />
                </svg>
                <span>約 {Math.round(totalWords / 10000)} 萬字</span>
              </div>
              {userProgress?.updated_at && (
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    上次閱讀: {new Date(userProgress.updated_at).toLocaleDateString('zh-TW')}
                  </span>
                </div>
              )}
            </div>

            {/* 操作按鈕 */}
            <div className="mt-auto flex flex-wrap items-center gap-3">
              {/* Primary CTA - 繼續閱讀 */}
              {completedChapters.length > 0 &&
                (lastReadChapter ? (
                  <Link
                    to={`/novels/${novel.id}/read/${lastReadChapter.id}`}
                    className="btn btn-primary btn-lg"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    繼續閱讀
                    <span className="text-white/70 text-sm">
                      第 {lastReadChapter.chapter_index} 章
                    </span>
                  </Link>
                ) : (
                  <Link
                    to={`/novels/${novel.id}/read/${completedChapters[0].id}`}
                    className="btn btn-primary btn-lg"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                    開始閱讀
                  </Link>
                ))}

              {/* Secondary - 收藏按鈕 */}
              <button
                onClick={onToggleFavorite}
                disabled={favoriteLoading}
                className={`btn btn-md ${isFavorited ? 'btn-secondary' : 'btn-outline'}`}
              >
                <svg
                  className="w-5 h-5"
                  fill={isFavorited ? 'currentColor' : 'none'}
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                {isFavorited ? '已收藏' : '收藏'}
              </button>

              {/* Ghost - 下載按鈕 */}
              {completedChapters.length > 0 && (
                <div className="flex gap-1">
                  <button
                    onClick={() => onDownload('zip')}
                    disabled={downloading}
                    className="btn btn-ghost btn-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    ZIP
                  </button>
                  <button
                    onClick={() => onDownload('txt')}
                    disabled={downloading}
                    className="btn btn-ghost btn-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    TXT
                  </button>
                </div>
              )}

              {/* AI 分析 */}
              <Link
                to={`/novels/${novel.id}/analysis`}
                className="btn btn-outline btn-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                AI 分析
              </Link>

              {/* Admin 操作 */}
              {isAdmin && (
                <div className="flex gap-2 ml-auto">
                  <button onClick={onShowNovelEditor} className="btn btn-outline btn-sm">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                    編輯
                  </button>
                  <button onClick={onShowSeriesManager} className="btn btn-outline btn-sm">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    {novel.series ? '變更系列' : '加入系列'}
                  </button>
                  {/* 爬蟲式小說：檢查更新按鈕 */}
                  {novel.source_type !== 'uploaded' &&
                    novel.source_url &&
                    (novel.status === 'completed' || novel.status === 'paused') && (
                      <button onClick={onShowSyncChapters} className="btn btn-outline btn-sm">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        檢查更新
                      </button>
                    )}
                  {novel.status === 'crawling' && (
                    <button onClick={onPause} className="btn btn-ghost btn-sm text-warning-600">
                      暫停
                    </button>
                  )}
                  {novel.status === 'paused' && (
                    <button onClick={onResume} className="btn btn-ghost btn-sm text-success-600">
                      繼續
                    </button>
                  )}
                  {/* 重新抓取空白章節 */}
                  {hasEmptyChapters && (
                    <button
                      onClick={onRecrawlEmpty}
                      disabled={recrawling}
                      className="btn btn-ghost btn-sm text-warning-600"
                    >
                      {recrawling ? '排程中...' : '重抓空白章節'}
                    </button>
                  )}
                  <button onClick={onDelete} className="btn btn-ghost btn-sm text-danger-600">
                    刪除
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
